// src/components/Presentation/PresentationPreview.js (New Component)
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ChevronLeft, 
  ChevronRight, 
  Maximize, 
  Minimize,
  Play,
  Eye,
  Share
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getPresentation, createLiveSession } from '../../services/presentationAPI';
import SlideRenderer from './SlideRenderer';
import toast from 'react-hot-toast';

const PresentationPreview = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [presentation, setPresentation] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    loadPresentation();
  }, [id, user]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousSlide();
          break;
        case 'Escape':
          if (isFullscreen) {
            exitFullscreen();
          }
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlideIndex, isFullscreen, presentation]);

  const loadPresentation = async () => {
    try {
      setIsLoading(true);
      const presentationData = await getPresentation(id);

      // Check if user has access (owner or public presentation)
      if (presentationData.authorId !== user?.uid && 
          !presentationData.isPublic && 
          !presentationData.allowPublicView) {
        toast.error('Access denied');
        navigate('/presentations');
        return;
      }

      setPresentation(presentationData);
    } catch (error) {
      toast.error('Error loading presentation');
      console.error(error);
      navigate('/presentations');
    } finally {
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    if (currentSlideIndex < (presentation?.slides?.length - 1)) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const previousSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const goToSlide = (slideIndex) => {
    setCurrentSlideIndex(slideIndex);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen && previewRef.current) {
      if (previewRef.current.requestFullscreen) {
        previewRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      exitFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    }
    setIsFullscreen(false);
  };

  const startLivePresentation = async () => {
    if (!user) {
      toast.error('Please sign in to start live presentation');
      return;
    }

    try {
      const session = await createLiveSession(id, user.uid);
      toast.success('Live session started!');
      navigate(`/presentation/${id}/present?session=${session.sessionCode}`);
    } catch (error) {
      toast.error('Error starting live presentation');
      console.error(error);
    }
  };

  const sharePresentation = async () => {
    const shareUrl = `${window.location.origin}/presentation/${id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: presentation?.title,
          text: 'Check out this presentation',
          url: shareUrl
        });
      } catch (error) {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard');
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading presentation...</p>
        </div>
      </div>
    );
  }

  if (!presentation) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p>Presentation not found</p>
        </div>
      </div>
    );
  }

  const currentSlide = presentation.slides?.[currentSlideIndex];

  return (
    <div ref={previewRef} className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Top Bar */}
      <div className="bg-black bg-opacity-50 backdrop-blur-sm p-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold">{presentation.title}</h1>
          <div className="flex items-center space-x-2 px-3 py-1 bg-blue-600 bg-opacity-20 rounded-lg">
            <Eye className="w-4 h-4" />
            <span className="text-sm">Preview Mode</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {user?.uid === presentation.authorId && (
            <button
              onClick={startLivePresentation}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center"
            >
              <Play className="w-4 h-4 mr-2" />
              Start Live
            </button>
          )}
          
          <button
            onClick={sharePresentation}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center"
          >
            <Share className="w-4 h-4 mr-2" />
            Share
          </button>
          
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Main Slide Display */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        {/* Navigation Arrows */}
        <button
          onClick={previousSlide}
          disabled={currentSlideIndex === 0}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full transition-colors disabled:opacity-25"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <button
          onClick={nextSlide}
          disabled={currentSlideIndex === (presentation.slides?.length - 1)}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full transition-colors disabled:opacity-25"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide Content */}
        <div className="w-full max-w-6xl mx-auto">
          {currentSlide ? (
            <div 
              className="w-full rounded-lg overflow-hidden shadow-2xl bg-white"
              style={{
                aspectRatio: presentation.aspectRatio === '4:3' ? '4/3' : '16/9'
              }}
            >
              <SlideRenderer
                slide={currentSlide}
                isLive={false}
                isPreview={true}
              />
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-400">No slides to display</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="bg-black bg-opacity-50 backdrop-blur-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm">
              Slide {currentSlideIndex + 1} of {presentation.slides?.length || 0}
            </span>
            
            {/* Slide thumbnails */}
            <div className="flex space-x-2 overflow-x-auto max-w-lg">
              {presentation.slides?.map((slide, index) => (
                <button
                  key={slide.id}
                  onClick={() => goToSlide(index)}
                  className={`flex-shrink-0 w-16 h-12 border-2 rounded overflow-hidden transition-colors ${
                    currentSlideIndex === index
                      ? 'border-white'
                      : 'border-gray-500 hover:border-gray-300'
                  }`}
                >
                  <div 
                    className="w-full h-full"
                    style={{ backgroundColor: slide.background?.value || '#ffffff' }}
                  >
                    {/* Mini slide preview */}
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-400">
              Use ← → arrows or space to navigate • F for fullscreen
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresentationPreview;